import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  User,
  Mail,
  Briefcase,
  CheckCircle,
  XCircle,
  Crown,
} from 'lucide-react';
import { Aprovador } from '../types';
import api from '../services/api';
import { useUserPermissions } from '../hooks/useUserPermissions';

const AprovadoresList: React.FC = () => {
  const navigate = useNavigate();
  const { isMaster } = useUserPermissions();
  const [aprovadores, setAprovadores] = useState<Aprovador[]>([]);
  const [filteredAprovadores, setFilteredAprovadores] = useState<Aprovador[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAtivo, setFilterAtivo] = useState<'todos' | 'ativo' | 'inativo'>('todos');

  const fetchAprovadores = async () => {
    try {
      let url = '/aprovadores';

      // Adicionar parâmetro baseado no filtro
      if (filterAtivo === 'ativo') {
        url += '?ativo=true';
      } else if (filterAtivo === 'inativo') {
        url += '?ativo=false';
      } else if (filterAtivo === 'todos') {
        url += '?ativo=all'; // Parâmetro especial para buscar todos
      }

      const response = await api.get<Aprovador[]>(url);
      setAprovadores(response.data);
    } catch (error) {
      toast.error('Erro ao carregar aprovadores');
      console.error('Erro:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterAprovadores = () => {
    let filtered = aprovadores;

    // Filtro por termo de busca (apenas busca local)
    if (searchTerm) {
      filtered = filtered.filter(
        aprovador =>
          aprovador.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
          aprovador.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (aprovador.cargo && aprovador.cargo.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    setFilteredAprovadores(filtered);
  };

  useEffect(() => {
    fetchAprovadores();
  }, [filterAtivo]);

  useEffect(() => {
    filterAprovadores();
  }, [aprovadores, searchTerm, filterAprovadores]);

  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja desativar este aprovador?')) return;

    try {
      await api.delete(`/aprovadores/${id}`);
      toast.success('Aprovador desativado com sucesso!');
      fetchAprovadores();
    } catch (error) {
      toast.error('Erro ao desativar aprovador');
      console.error('Erro:', error);
    }
  };

  const getStatusColor = (ativo: boolean) => {
    return ativo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  const getStatusText = (ativo: boolean) => {
    return ativo ? 'Ativo' : 'Inativo';
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600'></div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gray-50 py-8'>
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
        {/* Header */}
        <div className='mb-8'>
          <div className='flex items-center justify-between'>
            <div>
              <h1 className='text-3xl font-bold text-gray-900'>Aprovadores</h1>
              <p className='text-gray-600 mt-1'>Gerencie os aprovadores do sistema</p>
            </div>
            {isMaster && (
              <button
                onClick={() => navigate('/aprovadores/novo')}
                className='flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors'
              >
                <Plus className='h-4 w-4' />
                <span>Novo Aprovador</span>
              </button>
            )}
          </div>
        </div>

        {/* Filtros e Busca */}
        <div className='bg-white rounded-lg shadow mb-6'>
          <div className='p-6'>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
              {/* Busca */}
              <div className='relative'>
                <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400' />
                <input
                  type='text'
                  placeholder='Buscar aprovadores...'
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className='w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent'
                />
              </div>

              {/* Filtro de Status */}
              <div>
                <select
                  value={filterAtivo}
                  onChange={e => setFilterAtivo(e.target.value as 'todos' | 'ativo' | 'inativo')}
                  className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent'
                >
                  <option value='todos'>Todos os status</option>
                  <option value='ativo'>Apenas ativos</option>
                  <option value='inativo'>Apenas inativos</option>
                </select>
              </div>

              {/* Contador */}
              <div className='flex items-center justify-end'>
                <span className='text-sm text-gray-600'>
                  {filteredAprovadores.length} de {aprovadores.length} aprovadores
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Lista de Aprovadores */}
        <div className='bg-white rounded-lg shadow overflow-hidden'>
          {filteredAprovadores.length === 0 ? (
            <div className='text-center py-12'>
              <User className='mx-auto h-12 w-12 text-gray-400 mb-4' />
              <h3 className='text-lg font-medium text-gray-900 mb-2'>
                {searchTerm || filterAtivo !== 'todos'
                  ? 'Nenhum aprovador encontrado'
                  : 'Nenhum aprovador cadastrado'}
              </h3>
              <p className='text-gray-600 mb-6'>
                {searchTerm || filterAtivo !== 'todos'
                  ? 'Tente ajustar os filtros de busca'
                  : 'Comece criando o primeiro aprovador do sistema'}
              </p>
              {!searchTerm && filterAtivo === 'todos' && (
                <button
                  onClick={() => navigate('/aprovadores/novo')}
                  className='inline-flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors'
                >
                  <Plus className='h-4 w-4' />
                  <span>Criar Aprovador</span>
                </button>
              )}
            </div>
          ) : (
            <div className='overflow-x-auto'>
              <table className='min-w-full divide-y divide-gray-200'>
                <thead className='bg-gray-50'>
                  <tr>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Aprovador
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Cargo
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Email
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Perfil
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Status
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Data de Criação
                    </th>
                    <th className='px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className='bg-white divide-y divide-gray-200'>
                  {filteredAprovadores.map(aprovador => (
                    <tr key={aprovador.id} className='hover:bg-gray-50'>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <div className='flex items-center'>
                          <div className='flex-shrink-0 h-10 w-10'>
                            <div className='h-10 w-10 rounded-full bg-primary-600 flex items-center justify-center'>
                              <span className='text-sm font-medium text-white'>
                                {aprovador.nome.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className='ml-4'>
                            <div className='text-sm font-medium text-gray-900'>
                              {aprovador.nome}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <div className='flex items-center'>
                          <Briefcase className='h-4 w-4 text-gray-400 mr-2' />
                          <span className='text-sm text-gray-900'>{aprovador.cargo}</span>
                        </div>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <div className='flex items-center'>
                          <Mail className='h-4 w-4 text-gray-400 mr-2' />
                          <span className='text-sm text-gray-900'>{aprovador.email}</span>
                        </div>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        {aprovador.perfil_master ? (
                          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800'>
                            <Crown className='h-3 w-3 mr-1' />
                            Master
                          </span>
                        ) : (
                          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800'>
                            Padrão
                          </span>
                        )}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                            aprovador.ativo
                          )}`}
                        >
                          {aprovador.ativo ? (
                            <CheckCircle className='h-3 w-3 mr-1' />
                          ) : (
                            <XCircle className='h-3 w-3 mr-1' />
                          )}
                          {getStatusText(aprovador.ativo)}
                        </span>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
                        {new Date(aprovador.created_at).toLocaleDateString('pt-BR')}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-right text-sm font-medium'>
                        <div className='flex items-center justify-end space-x-2'>
                          <button
                            onClick={() => navigate(`/aprovadores/${aprovador.id}/editar`)}
                            className='text-primary-600 hover:text-primary-700 transition-colors'
                            title='Editar'
                          >
                            <Edit className='h-4 w-4' />
                          </button>
                          <button
                            onClick={() => handleDelete(aprovador.id)}
                            className='text-red-600 hover:text-red-800 transition-colors'
                            title='Desativar'
                          >
                            <Trash2 className='h-4 w-4' />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AprovadoresList;
